import { useState, useMemo } from 'react';
import { Plus, ClipboardList } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { ChecklistCard } from './ChecklistCard';
import { ChecklistFormModal } from './ChecklistFormModal';
import { computeSectionStatus, type SectionStatus } from '../utils/checklistStatus';
import type { ChecklistRespostaValor } from '@portal-leo/shared';

interface TabChecklistsProps {
  eventoId?: string;
  campanhaId?: string;
}

function useChecklistSectionStatuses(
  checklistId: string,
  comissaoMembroId: string | undefined,
): Array<{ nome: string; status: SectionStatus }> | undefined {
  const { data: fullChecklist } = trpc.checklists.getById.useQuery(
    { id: checklistId },
    { enabled: !!checklistId },
  );

  const { data: resposta } = trpc.checklists.getResposta.useQuery(
    { checklistId, comissaoMembroId: comissaoMembroId! },
    { enabled: !!comissaoMembroId },
  );

  return useMemo(() => {
    if (!fullChecklist) return undefined;
    const valores = (resposta?.valores as Record<string, ChecklistRespostaValor>) || {};
    return fullChecklist.secoes.map((secao) => ({
      nome: secao.nome,
      status: computeSectionStatus(secao.itens, valores),
    }));
  }, [fullChecklist, resposta]);
}

function ChecklistCardWithStatus({
  checklist,
  comissaoMembroId,
  comissaoNome,
  onEdit,
  onDelete,
  canEdit = true,
}: {
  checklist: { id: string; nome: string; descricao: string | null };
  comissaoMembroId: string | undefined;
  comissaoNome?: string;
  onEdit: () => void;
  onDelete: () => void;
  canEdit?: boolean;
}) {
  const sectionStatuses = useChecklistSectionStatuses(checklist.id, comissaoMembroId);
  return (
    <ChecklistCard
      checklist={{ ...checklist, comissaoNome }}
      sectionStatuses={sectionStatuses}
      onEdit={onEdit}
      onDelete={onDelete}
      canEdit={canEdit}
    />
  );
}

export function TabChecklists({ eventoId, campanhaId }: TabChecklistsProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('ADMIN', 'COORDENADOR_EVENTO');

  const utils = trpc.useUtils();

  const [comissaoFilter, setComissaoFilter] = useState<string>('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState<string | undefined>();

  // Load comissoes for filter dropdown
  const { data: comissoesData } = trpc.comissoes.list.useQuery({
    eventoId,
    campanhaId,
    page: 1,
    limit: 100,
  });
  const comissoesList = comissoesData?.items;

  // Load checklists filtered by comissao or all
  const { data: checklistsData, isLoading } = trpc.checklists.list.useQuery({
    comissaoId: comissaoFilter || undefined,
    eventoId: !comissaoFilter ? eventoId : undefined,
    campanhaId: !comissaoFilter ? campanhaId : undefined,
    page: 1,
    limit: 100,
  });
  const checklists = checklistsData?.items;

  const deleteMutation = trpc.checklists.delete.useMutation({
    onSuccess: () => {
      utils.checklists.list.invalidate();
      utils.checklists.countByEvento.invalidate();
    },
  });

  const handleEdit = (checklistId: string) => {
    setEditingChecklistId(checklistId);
    setIsFormModalOpen(true);
  };

  const handleDelete = (checklistId: string) => {
    if (confirm('Tem certeza que deseja excluir este checklist?')) {
      deleteMutation.mutate({ id: checklistId });
    }
  };

  const handleNewChecklist = () => {
    if (comissoesList && comissoesList.length > 0) {
      setEditingChecklistId(undefined);
      setIsFormModalOpen(true);
    } else {
      alert('Crie uma comissao antes de criar um checklist.');
    }
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingChecklistId(undefined);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Checklists</h3>
          <p className="text-sm text-gray-500">Gerencie os checklists das comissoes</p>
        </div>
        <Button onClick={handleNewChecklist} disabled={!canEdit}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Checklist
        </Button>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <select
          value={comissaoFilter}
          onChange={(e) => setComissaoFilter(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todas as comissoes</option>
          {comissoesList?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
        </div>
      ) : checklists?.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
          <ClipboardList className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Nenhum checklist encontrado</h3>
          <p className="text-sm text-gray-500 mb-4">
            {comissaoFilter ? 'Tente ajustar o filtro' : 'Comece criando um checklist para as comissoes'}
          </p>
          {!comissaoFilter && (
            <Button onClick={handleNewChecklist} disabled={!canEdit}>
              <Plus className="mr-2 h-4 w-4" />
              Criar Checklist
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {checklists?.map((checklist) => {
            const comissao = comissoesList?.find((c) => c.id === checklist.comissaoId);
            return (
              <ChecklistCardWithStatus
                key={checklist.id}
                checklist={checklist}
                comissaoMembroId={undefined}
                comissaoNome={comissao?.nome}
                onEdit={() => handleEdit(checklist.id)}
                onDelete={() => handleDelete(checklist.id)}
                canEdit={canEdit}
              />
            );
          })}
        </div>
      )}

      {/* Form Modal */}
      <ChecklistFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseFormModal}
        comissoes={comissoesList || []}
        defaultComissaoId={editingChecklistId
          ? checklists?.find((c) => c.id === editingChecklistId)?.comissaoId
          : comissaoFilter || undefined}
        checklistId={editingChecklistId}
      />
    </div>
  );
}
